/**
 * 出图确认 · 最近出图会话(MRU)纯列表代数 + localStorage 薄封装。
 *
 * 背景:页面状态 100% 来自 URL query(doc/pk/ds/part),服务端持久化一切。
 * bare `/app/drawing`(侧栏返回 / 刷新裸路由)原本死胡同空态 —— 这里记住可重开的
 * URL 上下文,让 bare 路由自动恢复最近一条 + 顶部「最近」切换器。
 *
 * 身份键 = 后端方案资源四元组(doc+part+pk+ds),不可省 pk/ds(否则同 doc 不同
 * 策略的两个独立服务端草稿会被合并,resume 到错误的那套)。
 *
 * key 作用域内嵌 user.id → 共享浏览器跨用户天然隔离(防零件名轻度泄漏);拿不到
 * user.id 时读写一律跳过(不落 anon 共享桶)。列表代数与 localStorage 分离 →
 * 前者 node 纯测。
 */

export const MRU_MAX = 8;
const MRU_SCHEMA_V = 1;
const KEY_PREFIX = "cnc.drawing.mru.";

export type DrawingMruStatus = "draft" | "confirmed" | "superseded" | "unknown";

export interface DrawingMruEntry {
  doc: string; // cad_document_id
  part: number; // -1 单件整图;>=0 装配逐件索引
  pk: string; // process kind(默认 "none")
  ds: string; // dim strategy(默认 "chain")
  label: string; // 人类可读主标签
  status: DrawingMruStatus;
  ts: number; // 最近 upsert 时刻(Date.now())
}

/** 去重 / 身份键 = 后端方案资源四元组。不可省 pk/ds。 */
export function mruKey(
  e: Pick<DrawingMruEntry, "doc" | "part" | "pk" | "ds">,
): string {
  return `${e.doc}#${e.part}#${e.pk}#${e.ds}`;
}

/** upsert:去重(同 key 覆盖)+ 新项置顶(ts desc)+ 截断 ≤ max。不可变返回。 */
export function upsertList(
  list: readonly DrawingMruEntry[],
  entry: DrawingMruEntry,
  max = MRU_MAX,
): DrawingMruEntry[] {
  const key = mruKey(entry);
  return [entry, ...list.filter((x) => mruKey(x) !== key)].slice(0, max);
}

/** 剔除某 key。不可变返回。 */
export function evictList(
  list: readonly DrawingMruEntry[],
  key: string,
): DrawingMruEntry[] {
  return list.filter((x) => mruKey(x) !== key);
}

/** 恢复候选:第一条(最近)不在 tried 集内的项;无 → null(保证终止性)。 */
export function pickRestoreCandidate(
  list: readonly DrawingMruEntry[],
  tried: ReadonlySet<string>,
): DrawingMruEntry | null {
  for (const e of list) {
    if (!tried.has(mruKey(e))) return e;
  }
  return null;
}

/** entry → 可重开 URL 查询参数(默认值省略,与 DrawingConfirmModule 读取默认严格一致)。 */
export function toSearchParams(entry: DrawingMruEntry): Record<string, string> {
  const out: Record<string, string> = { doc: entry.doc };
  if (entry.part >= 0) out.part = String(entry.part);
  if (entry.pk && entry.pk !== "none") out.pk = entry.pk;
  if (entry.ds && entry.ds !== "chain") out.ds = entry.ds;
  return out;
}

/* ── localStorage 薄封装(作用域隔离 · 全 try/catch 静默 · 脏数据过滤) ── */

interface MruEnvelope {
  v: number;
  items: DrawingMruEntry[];
}

function keyFor(scope: string): string {
  return `${KEY_PREFIX}${scope}`;
}

function isEntry(x: unknown): x is DrawingMruEntry {
  if (!x || typeof x !== "object") return false;
  const e = x as Record<string, unknown>;
  return (
    typeof e.doc === "string" &&
    typeof e.part === "number" &&
    typeof e.pk === "string" &&
    typeof e.ds === "string" &&
    typeof e.label === "string" &&
    typeof e.status === "string" &&
    typeof e.ts === "number"
  );
}

/** 读 · scope 缺失 / 无 localStorage / JSON 损坏 / 形状漂移 → 一律 []。 */
export function loadMru(scope: string | null | undefined): DrawingMruEntry[] {
  if (!scope) return [];
  try {
    const raw = globalThis.localStorage?.getItem(keyFor(scope));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as MruEnvelope | DrawingMruEntry[];
    // v1 用 {v,items} 信封;兼容裸数组(未来平滑迁移)。
    const items = Array.isArray(parsed) ? parsed : parsed?.items;
    if (!Array.isArray(items)) return [];
    return items.filter(isEntry);
  } catch {
    return [];
  }
}

function writeMru(scope: string, items: DrawingMruEntry[]): void {
  try {
    globalThis.localStorage?.setItem(
      keyFor(scope),
      JSON.stringify({ v: MRU_SCHEMA_V, items } satisfies MruEnvelope),
    );
  } catch {
    /* 隐私模式 / 配额满 · 静默降级 */
  }
}

/** upsert 到存储(读改写,最小化窗口)。scope 缺失 → no-op。 */
export function upsertMru(
  scope: string | null | undefined,
  entry: DrawingMruEntry,
): void {
  if (!scope) return;
  writeMru(scope, upsertList(loadMru(scope), entry));
}

/** 剔除某 key。scope 缺失 → no-op。 */
export function evictMru(scope: string | null | undefined, key: string): void {
  if (!scope) return;
  writeMru(scope, evictList(loadMru(scope), key));
}

/** 清空某用户的 MRU 桶(登出时调 · 零件名等不在共享终端残留)。scope 缺失 → no-op。 */
export function clearMru(scope: string | null | undefined): void {
  if (!scope) return;
  try {
    globalThis.localStorage?.removeItem(keyFor(scope));
  } catch {
    /* 隐私模式 · 静默 */
  }
}
